import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { MapContainer, TileLayer, Polyline, CircleMarker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useDebouncedCallback } from "use-debounce";

import logoSrc from "./images/logod.png";

const BRAND = {
  graphite:     "#2f2f2f",
  darkSlate:    "#305661",
  darkTeal:     "#1a404a",
  lightYellow:  "#fffbdb",
  lavenderGrey: "#7a82ab",
  vibrantCoral: "#fe5f55",
};

const C = {
  bg:           "#eef3f4",
  panel:        "#ffffff",
  panelAlt:     "#f2f6f7",
  faint:        "#e6eef0",
  border:       "#cdd8da",
  text:         BRAND.graphite,
  muted:        "#5e7478",
  teal:         BRAND.darkTeal,
  tealMid:      BRAND.darkSlate,
  tealLight:    "#d4e8ec",
  coral:        BRAND.vibrantCoral,
  coralLight:   "#fff0ef",
  lavender:     BRAND.lavenderGrey,
  lavenderLight:"#edeef7",
  amber:        "#c07a20",
  amberLight:   "#fdf3e3",
  green:        "#3a7d44",
  greenLight:   "#edf5ee",
  blue:         "#2563a8",
  blueLight:    "#e8f0fb",
  yellow:       BRAND.lightYellow,
};

const ROUTE_SHADOW_RGB = {
  "Serotonin Route":      "254,95,85",
  "Cortisol-Crush Route": "122,130,171",
  "Allergy-Safe Route":   "48,86,97",
};

const ROUTES = {
  "Serotonin Route":      { color: C.coral,    bg: C.coralLight,    icon: "☀️", tag: "Max sun & green space" },
  "Cortisol-Crush Route": { color: C.lavender,  bg: C.lavenderLight, icon: "🧘", tag: "Quietest & lowest stress" },
  "Allergy-Safe Route":   { color: C.tealMid,   bg: C.tealLight,     icon: "🌿", tag: "Lowest pollen & PM2.5" },
};

const ROUTE_BREAKDOWN = {
  "Serotonin Route": [
    { label: "UV & Sunlight", mult: 1.08, color: C.coral },
    { label: "Green Space",   mult: 1.01, color: C.green },
    { label: "Air Quality",   mult: 0.91, color: C.lavender },
    { label: "Noise Level",   mult: 0.71, color: C.amber },
  ],
  "Cortisol-Crush Route": [
    { label: "Noise Level",   mult: 1.10, color: C.green },
    { label: "Traffic Calm",  mult: 1.05, color: C.green },
    { label: "UV & Sunlight", mult: 0.74, color: C.amber },
    { label: "Green Space",   mult: 0.81, color: C.amber },
  ],
  "Allergy-Safe Route": [
    { label: "Pollen Index",  mult: 1.12, color: C.green },
    { label: "PM2.5",         mult: 1.05, color: C.green },
    { label: "UV & Sunlight", mult: 0.72, color: C.amber },
    { label: "Noise Level",   mult: 0.84, color: C.amber },
  ],
};

const PRESETS = {
  none:      { label: "Custom",        icon: "🫀", desc: "Balanced scoring" },
  asthmatic: { label: "The Asthmatic", icon: "💨", desc: "Weights PM2.5 & pollen 90%" },
  burnout:   { label: "The Burnout",   icon: "🧠", desc: "Weights noise & blue space 90%" },
  athlete:   { label: "The Athlete",   icon: "🏃", desc: "Weights UV & elevation 90%" },
};

const MOCK_ROUTES = [
  { type: "Serotonin Route", score: 87, distance_m: 2300, duration_s: 1650,
    steps: [
      { text: "Head north on Main St (800 m)",       way_points: [0, 4] },
      { text: "Turn left onto Park Ave (400 m)",      way_points: [4, 6] },
      { text: "Continue through Civic Park (600 m)", way_points: [6, 9] },
      { text: "Arrive at destination",                way_points: [9, 9] },
    ],
    geometry: { coordinates: [[-122.009,37.330],[-122.010,37.331],[-122.010,37.332],[-122.010,37.333],[-122.009,37.334],[-122.008,37.335],[-122.008,37.336],[-122.007,37.337],[-122.006,37.338],[-122.006,37.340]] } },
  { type: "Cortisol-Crush Route", score: 74, distance_m: 2600, duration_s: 1860,
    steps: [
      { text: "Head west on Oak St (700 m)",          way_points: [0, 3] },
      { text: "Turn right onto Quiet Lane (900 m)",   way_points: [3, 7] },
      { text: "Through residential zone (500 m)",     way_points: [7, 9] },
      { text: "Arrive at destination",                 way_points: [9, 9] },
    ],
    geometry: { coordinates: [[-122.009,37.330],[-122.010,37.330],[-122.012,37.331],[-122.013,37.332],[-122.013,37.333],[-122.013,37.334],[-122.012,37.335],[-122.012,37.337],[-122.008,37.338],[-122.006,37.340]] } },
  { type: "Allergy-Safe Route", score: 69, distance_m: 2100, duration_s: 1500,
    steps: [
      { text: "Head east on Elm St (500 m)",          way_points: [0, 3] },
      { text: "South on 3rd Ave (700 m)",             way_points: [3, 6] },
      { text: "Through low-pollen corridor (600 m)",  way_points: [6, 9] },
      { text: "Arrive at destination",                 way_points: [9, 9] },
    ],
    geometry: { coordinates: [[-122.009,37.330],[-122.008,37.330],[-122.007,37.331],[-122.006,37.332],[-122.005,37.333],[-122.005,37.334],[-122.005,37.336],[-122.005,37.337],[-122.006,37.338],[-122.006,37.340]] } },
];

const MOCK_ENV = { uv_index: 6.2, green_space_score: 0.71, air_quality_pm25: 8.4, noise_db: 48 };

const LOADING_QUIPS = [
  "Sniffing out the pollen levels... 🌸",
  "Asking the trees how they're feeling...",
  "Calibrating your cortisol shield... 🧠",
  "Checking if that park is actually vibes...",
  "Bribing pigeons for noise data... 🐦",
  "Measuring sun exposure to the millimeter...",
  "Finding the route that sparks serotonin... ☀️",
  "Consulting the UV index oracle...",
  "Counting blades of grass for green score... 🌿",
  "Shushing the loud streets... 🔇",
];

async function geocode(query, biasCoords = null) {
  const params = new URLSearchParams({
    q: query, format: "json", limit: "7", addressdetails: "1", "accept-language": "en",
  });
  if (biasCoords) {
    const d = 0.25;
    params.set("viewbox", [biasCoords.lng-d, biasCoords.lat+d, biasCoords.lng+d, biasCoords.lat-d].join(","));
    params.set("bounded", "0");
  }
  const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, { headers: { "Accept-Language": "en" } });
  const results = await res.json();
  if (biasCoords && results.length > 1) {
    const haversine = (a, b) => {
      const R = 6371, dLat = (b.lat-a.lat)*Math.PI/180, dLng = (b.lng-(a.lng??a.lon))*Math.PI/180,
            sinLat = Math.sin(dLat/2), sinLng = Math.sin(dLng/2);
      return R*2*Math.asin(Math.sqrt(sinLat*sinLat+Math.cos(a.lat*Math.PI/180)*Math.cos(b.lat*Math.PI/180)*sinLng*sinLng));
    };
    results.sort((a,b) =>
      haversine(biasCoords,{lat:parseFloat(a.lat),lng:parseFloat(a.lon)}) -
      haversine(biasCoords,{lat:parseFloat(b.lat),lng:parseFloat(b.lon)})
    );
  }
  return results;
}

/* ══════════════════════════════════════════════════════════════════
   MAP CONTROLLER
══════════════════════════════════════════════════════════════════ */
function MapController({ fitTarget, isMobile }) {
  const map = useMap();
  useEffect(() => {
    if (!fitTarget || fitTarget.length < 2) return;
    map.fitBounds(fitTarget, {
      paddingTopLeft:     isMobile ? [20, 20]  : [340, 60],
      paddingBottomRight: isMobile ? [20, 300] : [60, 60],
      maxZoom: 17, animate: true,
    });
  }, [fitTarget, map, isMobile]);
  return null;
}

/* ══════════════════════════════════════════════════════════════════
   LOADING STATE
══════════════════════════════════════════════════════════════════ */
function LoadingState() {
  const [quipIdx, setQuipIdx] = useState(0);
  const [fade, setFade] = useState(true);
  useEffect(() => {
    const t = setInterval(() => {
      setFade(false);
      setTimeout(() => { setQuipIdx(i => (i+1) % LOADING_QUIPS.length); setFade(true); }, 200);
    }, 2000);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:"36px 16px 28px", gap:18 }}>
      <div style={{ position:"relative", width:52, height:52 }}>
        <div style={{ width:52, height:52, borderRadius:"50%", border:`3px solid ${C.tealLight}`, borderTopColor:C.teal, animation:"spin 0.85s linear infinite" }} />
        <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>🗺️</div>
      </div>
      <div style={{ fontSize:13, color:C.muted, textAlign:"center", lineHeight:1.6, minHeight:44, padding:"0 8px", opacity:fade?1:0, transition:"opacity 0.2s ease", fontStyle:"italic" }}>
        {LOADING_QUIPS[quipIdx]}
      </div>
      <div style={{ display:"flex", gap:6, alignItems:"center" }}>
        {[0,1,2].map(i => (
          <div key={i} style={{ width:6, height:6, borderRadius:"50%", background:C.teal, animation:`pulseDot 1.2s ease-in-out ${i*0.2}s infinite` }} />
        ))}
      </div>
      <div style={{ fontSize:11, color:C.border, textAlign:"center", letterSpacing:"0.3px" }}>
        Pulling live UV · pollen · noise · green space
      </div>
      <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:8, marginTop:4 }}>
        {[0,1,2].map(i => (
          <div key={i} style={{ borderRadius:12, padding:"12px 14px", border:`1.5px solid ${C.border}`, background:C.panelAlt, animation:`shimmer 1.5s ease-in-out ${i*0.15}s infinite alternate` }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:36, height:36, borderRadius:9, background:C.faint }} />
              <div style={{ flex:1 }}>
                <div style={{ width:"60%", height:10, borderRadius:4, background:C.faint, marginBottom:6 }} />
                <div style={{ width:"40%", height:8, borderRadius:4, background:C.faint }} />
              </div>
              <div style={{ width:32, height:32, borderRadius:6, background:C.faint }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   WEATHER BANNER
══════════════════════════════════════════════════════════════════ */
function WeatherBanner({ envData }) {
  if (!envData) return null;
  const alerts = [];
  if ((envData.uv_index??0) >= 7) alerts.push({ icon:"🕶️", text:`UV is ${(envData.uv_index).toFixed(1)} today — Allergy-Safe route minimises exposed time`, urgency:"warn" });
  if ((envData.air_quality_pm25??0) >= 20) alerts.push({ icon:"😷", text:`PM2.5 at ${(envData.air_quality_pm25).toFixed(1)} µg — Cortisol-Crush route hugs cleaner side streets`, urgency:"warn" });
  if ((envData.noise_db??0) >= 60) alerts.push({ icon:"🔇", text:`Noise at ${Math.round(envData.noise_db)} dB — Cortisol-Crush Route keeps you on quiet roads`, urgency:"info" });
  if ((envData.green_space_score??0) >= 0.7) alerts.push({ icon:"🌳", text:"Great green space today — Serotonin Route is peak vibes right now", urgency:"good" });
  if ((envData.uv_index??0) < 3 && (envData.noise_db??0) < 45) alerts.push({ icon:"✨", text:"Low UV and quiet streets — perfect conditions for any route!", urgency:"good" });
  if (alerts.length === 0) return null;
  const alert = alerts[0];
  const colors = { warn:{bg:C.amberLight,border:C.amber,text:C.amber}, info:{bg:C.blueLight,border:C.blue,text:C.blue}, good:{bg:C.greenLight,border:C.green,text:C.green} }[alert.urgency];
  return (
    <div style={{ margin:"8px 0 0", padding:"9px 12px", borderRadius:10, border:`1px solid ${colors.border}`, background:colors.bg, display:"flex", gap:8, alignItems:"flex-start", animation:"fadeIn 0.4s ease" }}>
      <span style={{ fontSize:15, flexShrink:0 }}>{alert.icon}</span>
      <span style={{ fontSize:11, color:colors.text, lineHeight:1.5, fontWeight:500 }}>{alert.text}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   ADDRESS INPUT
══════════════════════════════════════════════════════════════════ */
function AddressInput({ placeholder, value, onChange, onSelect, dot, biasCoords }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const fetch_ = useDebouncedCallback(async (q) => {
    if (q.length < 3) { setSuggestions([]); return; }
    const r = await geocode(q, biasCoords ?? null);
    setSuggestions(r); setOpen(true);
  }, 380);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const getLabel = (s) => {
    const a = s.address ?? {};
    const primary = [a.house_number, a.road||a.pedestrian||a.footway||a.path].filter(Boolean).join(" ") || s.display_name.split(",")[0];
    const secondary = [a.neighbourhood||a.suburb||a.quarter, a.city||a.town||a.village||a.municipality, a.country].filter(Boolean).slice(0,2).join(", ");
    return { primary, secondary };
  };
  return (
    <div ref={ref} style={{ position:"relative", display:"flex", alignItems:"center", gap:10 }}>
      <span style={{ width:9, height:9, borderRadius:"50%", flexShrink:0, background:dot==="A"?C.teal:C.coral, boxShadow:`0 0 0 3px ${dot==="A"?C.tealLight:C.coralLight}` }} />
      <input value={value}
        onChange={e => { onChange(e.target.value); fetch_(e.target.value); }}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        style={{ border:"none", outline:"none", background:"transparent", fontSize:16, color:C.text, width:"100%", padding:"8px 0", fontFamily:"'DM Sans', sans-serif" }} />
      {open && suggestions.length > 0 && (
        <div style={{ position:"absolute", top:"calc(100% + 6px)", left:-16, right:-16, background:C.panel, borderRadius:12, zIndex:9999, boxShadow:"0 16px 40px rgba(0,0,0,0.14)", border:`0.5px solid ${C.border}`, overflow:"hidden" }}>
          {suggestions.map((s, i) => {
            const { primary, secondary } = getLabel(s);
            return (
              <div key={i}
                onClick={() => { onSelect({lat:parseFloat(s.lat),lng:parseFloat(s.lon)}); onChange(primary+(secondary?`, ${secondary.split(",")[0]}`:"")); setOpen(false); setSuggestions([]); }}
                style={{ padding:"12px 16px", fontSize:13, color:C.text, cursor:"pointer", borderBottom:i<suggestions.length-1?`0.5px solid ${C.border}`:"none", transition:"background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background=C.bg}
                onMouseLeave={e => e.currentTarget.style.background=C.panel}>
                <div style={{ fontWeight:500 }}>{primary}</div>
                {secondary && <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{secondary}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PulseMarker({ position, color }) {
  return (
    <>
      <CircleMarker center={position} radius={14} pathOptions={{ color, weight:1.5, fillColor:color, fillOpacity:0.15, className:"pulse-ring" }} />
      <CircleMarker center={position} radius={7} pathOptions={{ color:"#fff", weight:2, fillColor:color, fillOpacity:1 }} />
    </>
  );
}

function ScoreArc({ score, color }) {
  const pct = score/100, r=20, cx=28, cy=28, circ=2*Math.PI*r;
  return (
    <svg width={56} height={56} viewBox="0 0 56 56">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={C.border} strokeWidth={4} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={4}
        strokeDasharray={`${circ*pct} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${cx} ${cy})`} style={{ transition:"stroke-dasharray 0.6s ease" }} />
      <text x={cx} y={cy+5} textAnchor="middle" fontSize={13} fontWeight={700} fill={color}
        style={{ fontFamily:"'DM Sans', sans-serif" }}>{Math.round(score)}</text>
    </svg>
  );
}

function SectionLabel({ label, first=false }) {
  return (
    <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.9px", textTransform:"uppercase", color:C.muted, marginBottom:8, marginTop:first?0:16 }}>{label}</div>
  );
}

function Chip({ label, icon, active, onClick, color=C.teal, bg=C.tealLight }) {
  return (
    <div onClick={onClick} style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 12px", borderRadius:8, cursor:"pointer", userSelect:"none", border:`1.5px solid ${active?color:C.border}`, background:active?bg:C.panelAlt, transition:"all 0.13s" }}>
      <span style={{ fontSize:14 }}>{icon}</span>
      <span style={{ fontSize:12, fontWeight:active?600:400, color:active?color:C.muted }}>{label}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   ROUTE DETAIL PANEL
══════════════════════════════════════════════════════════════════ */
function RouteDetail({ route, onClose, onStartNav, isMobile }) {
  if (!route) return null;
  const meta = ROUTES[route.type];
  const breakdown = ROUTE_BREAKDOWN[route.type] ?? [];
  return (
    <div style={{
      position:"absolute", zIndex:1100, background:C.panel,
      display:"flex", flexDirection:"column",
      borderRight: isMobile?"none":`0.5px solid ${C.border}`,
      borderTop: isMobile?`0.5px solid ${C.border}`:"none",
      borderRadius: isMobile?"20px 20px 0 0":0,
      boxShadow: isMobile?"0 -4px 24px rgba(26,64,74,0.12)":"4px 0 24px rgba(26,64,74,0.10)",
      ...(isMobile ? { bottom:0, left:0, right:0, height:"75vh" } : { top:0, left:320, bottom:0, width:300 }),
      animation:"slideIn 0.2s ease",
    }}>
      {isMobile && <div style={{ width:36, height:4, borderRadius:2, background:C.border, margin:"12px auto 4px", flexShrink:0 }} />}
      <div style={{ padding:"16px", borderBottom:`0.5px solid ${C.border}`, display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
        <button onClick={onClose} style={{ background:C.faint, border:"none", borderRadius:8, padding:"8px 12px", cursor:"pointer", fontSize:14, color:C.muted, fontFamily:"'DM Sans', sans-serif", minHeight:44 }}>← Back</button>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:13, color:C.text }}>{route.type}</div>
          <div style={{ fontSize:11, color:C.muted }}>{meta.tag}</div>
        </div>
        <span style={{ fontSize:20 }}>{meta.icon}</span>
      </div>
      <div style={{ flex:1, overflowY:"auto", padding:"14px", background:C.bg }}>
        <div style={{ display:"flex", gap:7, marginBottom:14 }}>
          {[
            { label:"Distance", value:`${(route.distance_m/1000).toFixed(1)} km` },
            { label:"Walk time", value:`${Math.round(route.duration_s/60)} min` },
            { label:"Score", value:`${Math.round(route.score)}/100` },
          ].map(({ label, value }) => (
            <div key={label} style={{ flex:1, background:C.panel, borderRadius:10, padding:"9px 10px", textAlign:"center", border:`0.5px solid ${C.border}` }}>
              <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{value}</div>
              <div style={{ fontSize:9, color:C.muted, marginTop:2, textTransform:"uppercase", letterSpacing:"0.4px" }}>{label}</div>
            </div>
          ))}
        </div>
        <div style={{ background:C.panel, borderRadius:12, padding:"14px", border:`0.5px solid ${C.border}`, marginBottom:14, display:"flex", alignItems:"center", gap:14 }}>
          <ScoreArc score={route.score} color={meta.color} />
          <div>
            <div style={{ fontWeight:700, fontSize:12, color:C.text, marginBottom:3 }}>Biological Score</div>
            <div style={{ fontSize:11, color:C.muted, lineHeight:1.5 }}>Scored {Math.round(route.score)}/100 based on UV, green space, air quality, and noise along this path.</div>
          </div>
        </div>
        {breakdown.length > 0 && (
          <div style={{ background:C.panel, borderRadius:12, padding:"14px", border:`0.5px solid ${C.border}`, marginBottom:14 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.6px", textTransform:"uppercase", color:C.muted, marginBottom:10 }}>Why this score?</div>
            {breakdown.map(({ label, mult, color }) => {
              const val = Math.min(99, Math.round(route.score*mult));
              return (
                <div key={label} style={{ marginBottom:9 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                    <span style={{ fontSize:11, color:C.muted }}>{label}</span>
                    <span style={{ fontSize:11, fontWeight:700, color }}>{val}</span>
                  </div>
                  <div style={{ background:C.faint, borderRadius:3, height:4, overflow:"hidden" }}>
                    <div style={{ width:`${val}%`, height:"100%", background:color, borderRadius:3, transition:"width 0.6s ease" }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {route.steps?.length > 0 && (
          <div style={{ background:C.panel, borderRadius:12, padding:"14px", border:`0.5px solid ${C.border}` }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.6px", textTransform:"uppercase", color:C.muted, marginBottom:10 }}>Directions</div>
            {route.steps.map((step, i) => {
              const text = typeof step==="string"?step:step.text;
              return (
                <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                    <div style={{ width:20, height:20, borderRadius:"50%", flexShrink:0, background:i===0?C.teal:i===route.steps.length-1?meta.color:C.border, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:"white", fontWeight:700 }}>
                      {i===route.steps.length-1?"★":i+1}
                    </div>
                    {i<route.steps.length-1 && <div style={{ width:1.5, flex:1, background:C.border, minHeight:18, margin:"2px 0" }} />}
                  </div>
                  <div style={{ paddingBottom:i<route.steps.length-1?10:0, paddingTop:2 }}>
                    <div style={{ fontSize:13, color:C.text, lineHeight:1.4 }}>{text}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ padding:"12px 14px", borderTop:`0.5px solid ${C.border}`, background:C.panel, flexShrink:0 }}>
        <button onClick={onStartNav} style={{ width:"100%", padding:"14px", background:meta.color, color:"white", border:"none", borderRadius:12, fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"'DM Sans', sans-serif", minHeight:44 }}>
          Start Navigation →
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   NAVIGATION PANEL
══════════════════════════════════════════════════════════════════ */
function NavigationPanel({ route, onClose, onFitBounds, isMobile }) {
  const [stepIdx, setStepIdx] = useState(0);
  const meta = ROUTES[route.type];
  const steps = useMemo(() => route.steps ?? [], [route.steps]);
  const total = steps.length;
  const onFitBoundsRef = useRef(onFitBounds);
  useEffect(() => { onFitBoundsRef.current = onFitBounds; }, [onFitBounds]);

  const goToStep = useCallback((idx) => {
    const clamped = Math.max(0, Math.min(idx, total-1));
    setStepIdx(clamped);
    const step = steps[clamped];
    const allCoords = route.geometry.coordinates;
    let segCoords = [];
    if (step && step.way_points && step.way_points.length >= 2) {
      const [from, to] = step.way_points;
      segCoords = allCoords.slice(from, to+1).map(([lng,lat]) => [lat,lng]);
    }
    if (segCoords.length < 2) segCoords = allCoords.map(([lng,lat]) => [lat,lng]);
    onFitBoundsRef.current(segCoords);
  }, [steps, route.geometry.coordinates, total]);

  useEffect(() => { goToStep(0); }, [goToStep]);

  const currentStep = steps[stepIdx];
  const stepText = currentStep?(typeof currentStep==="string"?currentStep:currentStep.text):"";

  return (
    <div style={{
      position:"absolute", zIndex:1200, background:C.panel,
      display:"flex", flexDirection:"column",
      borderRight: isMobile?"none":`0.5px solid ${C.border}`,
      borderTop: isMobile?`0.5px solid ${C.border}`:"none",
      borderRadius: isMobile?"20px 20px 0 0":0,
      boxShadow: isMobile?"0 -4px 24px rgba(26,64,74,0.12)":"4px 0 24px rgba(26,64,74,0.12)",
      ...(isMobile ? { bottom:0, left:0, right:0, height:"55vh" } : { top:0, left:320, bottom:0, width:300 }),
    }}>
      {isMobile && <div style={{ width:36, height:4, borderRadius:2, background:"rgba(255,255,255,0.4)", margin:"12px auto 4px", flexShrink:0 }} />}
      <div style={{ padding:"16px", background:meta.color, color:"white", flexShrink:0, borderRadius:isMobile?"16px 16px 0 0":0 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
          <span style={{ fontSize:12, fontWeight:700, opacity:0.85, letterSpacing:"0.5px", textTransform:"uppercase" }}>{meta.icon} Navigating</span>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.2)", border:"none", borderRadius:6, padding:"6px 10px", cursor:"pointer", color:"white", fontSize:13, fontFamily:"'DM Sans', sans-serif", minHeight:36 }}>✕ End</button>
        </div>
        <div style={{ fontSize:14, fontWeight:700, lineHeight:1.4 }}>{stepText}</div>
        <div style={{ fontSize:11, opacity:0.75, marginTop:4 }}>Step {stepIdx+1} of {total}</div>
      </div>
      <div style={{ height:3, background:`${meta.color}30` }}>
        <div style={{ height:"100%", background:meta.color, width:`${((stepIdx+1)/total)*100}%`, transition:"width 0.3s ease" }} />
      </div>
      <div style={{ flex:1, overflowY:"auto", padding:"12px", background:C.bg }}>
        {steps.map((step, i) => {
          const text = typeof step==="string"?step:step.text;
          const isActive = i===stepIdx;
          return (
            <div key={i} onClick={() => goToStep(i)} style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"10px", borderRadius:10, cursor:"pointer", background:isActive?meta.bg:"transparent", border:`1.5px solid ${isActive?meta.color:"transparent"}`, marginBottom:4, transition:"all 0.15s" }}>
              <div style={{ width:22, height:22, borderRadius:"50%", flexShrink:0, background:isActive?meta.color:i<stepIdx?C.teal:C.border, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:"white", fontWeight:700, marginTop:1 }}>
                {i===total-1?"★":i<stepIdx?"✓":i+1}
              </div>
              <div style={{ fontSize:12, color:isActive?C.text:C.muted, fontWeight:isActive?600:400, lineHeight:1.4 }}>{text}</div>
            </div>
          );
        })}
      </div>
      <div style={{ padding:"12px 14px", borderTop:`0.5px solid ${C.border}`, background:C.panel, display:"flex", gap:8, flexShrink:0 }}>
        <button disabled={stepIdx===0} onClick={() => goToStep(stepIdx-1)} style={{ flex:1, padding:"12px", background:stepIdx===0?C.faint:C.panelAlt, color:stepIdx===0?C.muted:C.text, border:`0.5px solid ${C.border}`, borderRadius:10, fontWeight:600, fontSize:13, cursor:stepIdx===0?"not-allowed":"pointer", fontFamily:"'DM Sans', sans-serif", minHeight:44 }}>← Back</button>
        <button disabled={stepIdx===total-1} onClick={() => goToStep(stepIdx+1)} style={{ flex:2, padding:"12px", background:stepIdx===total-1?C.faint:meta.color, color:"white", border:"none", borderRadius:10, fontWeight:700, fontSize:13, cursor:stepIdx===total-1?"not-allowed":"pointer", fontFamily:"'DM Sans', sans-serif", opacity:stepIdx===total-1?0.5:1, minHeight:44 }}>
          {stepIdx===total-1?"Arrived ★":"Next →"}
        </button>
      </div>
    </div>
  );
}

const TILES = {
  standard: { url:"https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution:'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' },
  terrain:  { url:"https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",   attribution:'&copy; <a href="https://opentopomap.org">OpenTopoMap</a>' },
};

/* ══════════════════════════════════════════════════════════════════
   MAIN APP
══════════════════════════════════════════════════════════════════ */
export default function App() {
  const [startText, setStartText]     = useState("");
  const [endText, setEndText]         = useState("");
  const [startCoords, setStartCoords] = useState(null);
  const [endCoords, setEndCoords]     = useState(null);
  const [routes, setRoutes]           = useState([]);
  const [envData, setEnvData]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [activeRoute, setActiveRoute] = useState(null);
  const [showDetail, setShowDetail]   = useState(false);
  const [navActive, setNavActive]     = useState(false);
  const [terrainMode, setTerrainMode] = useState(false);
  const [preset, setPreset]           = useState("none");
  const [prefs, setPrefs]             = useState({ avoid_stroads:false, avoid_bridges:false, prefer_low_traffic:false, prefer_paths:false, round_trip:false });
  const [fitTarget, setFitTarget]     = useState(null);
  const [activeSegmentCoords, setActiveSegmentCoords] = useState(null);
  const [isMobile, setIsMobile]       = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const toggle    = (key) => setPrefs(p => ({ ...p, [key]:!p[key] }));
  const best      = routes.length > 0 ? routes.reduce((a,b) => a.score>b.score?a:b) : null;
  const activeObj = routes.find(r => r.type===activeRoute);

  const handleFitBounds = useCallback((segCoords) => {
    setActiveSegmentCoords(segCoords);
    setFitTarget(segCoords);
  }, []);

  const uvColor    = (v)  => (v??0)<3?C.green:(v??0)<6?C.amber:C.coral;
  const noiseColor = (db) => (db??0)<45?C.green:(db??0)<65?C.amber:C.coral;
  const uvLabel    = (v)  => (v??0)<3?"Low":(v??0)<6?"Moderate":(v??0)<8?"High":"Very High";
  const greenLabel = (s)  => (s??0)<0.3?"Sparse":(s??0)<0.6?"Moderate":"Good";
  const aqLabel    = (pm) => (pm??0)<12?"Good":(pm??0)<35?"Moderate":"Poor";
  const noiseLabel = (db) => (db??0)<45?"Quiet":(db??0)<65?"Moderate":"Loud";

  useEffect(() => {
    const url = process.env.REACT_APP_API_URL || "http://localhost:8000";
    fetch(`${url}/`).catch(() => {});
  }, []);

  const fetchRoutes = async () => {
    if (!startCoords || !endCoords) { setError("Pick a start and destination from the suggestions."); return; }
    setError(null); setLoading(true); setRoutes([]);
    try {
      const params = new URLSearchParams({
        start_lat:startCoords.lat, start_lng:startCoords.lng,
        end_lat:endCoords.lat, end_lng:endCoords.lng, preset,
        ...Object.fromEntries(Object.entries(prefs).filter(([,v])=>v).map(([k])=>[k,"true"])),
      });
      const backendUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const res = await fetch(`${backendUrl}/score?${params}`);
      const data = await res.json();
      if (data.error) { setError(data.error); setLoading(false); return; }
      setRoutes(data.routes); setEnvData(data.env_data);
      setActiveRoute(data.routes[0]?.type);
      if (data.routes[0]) setFitTarget(data.routes[0].geometry.coordinates.map(([lng,lat])=>[lat,lng]));
    } catch {
      setRoutes(MOCK_ROUTES); setEnvData(MOCK_ENV);
      setActiveRoute(MOCK_ROUTES[0]?.type);
      setFitTarget(MOCK_ROUTES[0].geometry.coordinates.map(([lng,lat])=>[lat,lng]));
    }
    setLoading(false);
  };

  const handleSelectRoute = (routeType) => {
    setActiveRoute(routeType); setActiveSegmentCoords(null);
    const r = routes.find(rt => rt.type===routeType);
    if (r) setFitTarget(r.geometry.coordinates.map(([lng,lat])=>[lat,lng]));
  };

  const allCoords  = routes.flatMap(r => r.geometry.coordinates);
  const firstCoord = allCoords[0] ?? null;
  const lastCoord  = allCoords[allCoords.length-1] ?? null;

  return (
    <div style={{ position:"relative", width:"100vw", height:"100vh", fontFamily:"'DM Sans', 'Segoe UI', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        body { overflow:hidden; position:fixed; width:100%; }
        input::placeholder { color:#9ab4b8; font-family:'DM Sans',sans-serif; }
        input { font-size:16px !important; }
        button { min-height:44px; }
        .leaflet-control-attribution { font-size:9px !important; }
        .leaflet-control-zoom { display:none; }
        @keyframes fadeIn  { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideIn { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
        @keyframes spin    { to { transform:rotate(360deg); } }
        @keyframes pulseDot { 0%,100%{transform:scale(1);opacity:0.4} 50%{transform:scale(1.5);opacity:1} }
        @keyframes shimmer  { from{opacity:0.6} to{opacity:1.0} }
        @keyframes pulseRing { 0%{transform:scale(1);opacity:.55} 70%{transform:scale(1.9);opacity:0} 100%{transform:scale(1.9);opacity:0} }
        .rcard { transition:box-shadow 0.14s, transform 0.14s; }
        .rcard:hover { transform:translateY(-1px); }
        .pulse-ring path { animation:pulseRing 2s cubic-bezier(0.215,0.61,0.355,1) infinite; transform-origin:center; transform-box:fill-box; }
        ::-webkit-scrollbar { width:3px; }
        ::-webkit-scrollbar-thumb { background:${C.border}; border-radius:3px; }
      `}</style>

      {/* MAP */}
      <MapContainer center={[37.3317,-122.009]} zoom={14} style={{ position:"absolute", inset:0, width:"100%", height:"100%" }} zoomControl={false}>
        <TileLayer key={terrainMode?"terrain":"standard"} url={terrainMode?TILES.terrain.url:TILES.standard.url} attribution={terrainMode?TILES.terrain.attribution:TILES.standard.attribution} />
        <MapController fitTarget={fitTarget} isMobile={isMobile} />
        {routes.map(route => (
          <Polyline key={route.type} positions={route.geometry.coordinates.map(([lng,lat])=>[lat,lng])} color={ROUTES[route.type].color} weight={activeRoute===route.type?6:3} opacity={activeRoute===route.type?0.35:0.15} />
        ))}
        {activeObj && <Polyline positions={activeObj.geometry.coordinates.map(([lng,lat])=>[lat,lng])} color={ROUTES[activeObj.type].color} weight={6} opacity={navActive?0.25:0.85} />}
        {navActive && activeSegmentCoords && activeSegmentCoords.length >= 2 && (
          <Polyline positions={activeSegmentCoords} color={activeObj?ROUTES[activeObj.type].color:C.teal} weight={8} opacity={1} />
        )}
        {firstCoord && !navActive && <PulseMarker position={[firstCoord[1],firstCoord[0]]} color={C.teal} />}
        {lastCoord && !navActive && <PulseMarker position={[lastCoord[1],lastCoord[0]]} color={activeObj?ROUTES[activeObj.type].color:C.coral} />}
      </MapContainer>

      {/* SIDEBAR */}
      <div style={{
        position:"absolute", zIndex:1000, background:C.panel,
        display:"flex", flexDirection:"column",
        boxShadow: isMobile?"0 -4px 24px rgba(26,64,74,0.12)":"4px 0 24px rgba(26,64,74,0.10)",
        borderRight: isMobile?"none":`0.5px solid ${C.border}`,
        borderTop: isMobile?`0.5px solid ${C.border}`:"none",
        borderRadius: isMobile?"20px 20px 0 0":0,
        ...(isMobile ? { bottom:0, left:0, right:0, height:"52vh", overflowY:"auto" } : { top:0, left:0, bottom:0, width:320, overflowY:"auto" })
      }}>
        {/* Drag handle on mobile */}
        {isMobile && <div style={{ width:36, height:4, borderRadius:2, background:C.border, margin:"10px auto 6px", flexShrink:0 }} />}

        {/* Sticky header */}
        <div style={{ padding:"18px 18px 14px", borderBottom:`0.5px solid ${C.border}`, background:C.panel, position:"sticky", top:0, zIndex:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
            <img src={logoSrc} alt="Map1" style={{ height:36, width:"auto", display:"block" }} />
            <button onClick={() => setTerrainMode(t => !t)} title="Toggle terrain map" style={{
              marginLeft:"auto", border:`0.5px solid ${terrainMode?C.teal:C.border}`,
              background:terrainMode?C.tealLight:C.panelAlt, borderRadius:8, padding:"5px 9px",
              cursor:"pointer", fontSize:14, color:terrainMode?C.teal:C.muted, transition:"all 0.13s"
            }}>⛰️</button>
          </div>
          <div style={{ background:C.faint, borderRadius:12, padding:"2px 14px", border:`0.5px solid ${C.border}` }}>
            <AddressInput placeholder="Starting point" value={startText} onChange={setStartText} onSelect={setStartCoords} dot="A" biasCoords={null} />
            <div style={{ height:"0.5px", background:C.border, margin:"0 -14px 0 8px" }} />
            <AddressInput placeholder="Where to?" value={endText} onChange={setEndText} onSelect={setEndCoords} dot="B" biasCoords={startCoords} />
          </div>
          {error && <div style={{ marginTop:8, padding:"8px 12px", fontSize:12, color:C.coral, background:C.coralLight, borderRadius:8, border:`0.5px solid ${C.coral}33` }}>⚠ {error}</div>}
          <WeatherBanner envData={envData} />
          <button onClick={fetchRoutes} disabled={loading} style={{
            width:"100%", marginTop:10, padding:"13px",
            background:loading?"#9ab4b8":C.teal, color:"#fff", border:"none", borderRadius:10,
            fontWeight:700, fontSize:14, cursor:loading?"not-allowed":"pointer",
            fontFamily:"'DM Sans', sans-serif", transition:"background 0.2s, transform 0.1s",
          }}
          onMouseDown={e => { if(!loading) e.currentTarget.style.transform="scale(0.98)"; }}
          onMouseUp={e => { e.currentTarget.style.transform="scale(1)"; }}>
            {loading?"Finding routes…":"Find My Route"}
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex:1, overflowY:"auto", padding:"16px 16px 24px", background:C.bg }}>
          <SectionLabel label="Health Profile" first />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:4 }}>
            {Object.entries(PRESETS).map(([key,p]) => (
              <div key={key} onClick={() => setPreset(key)} style={{ padding:"12px", borderRadius:10, cursor:"pointer", border:`1.5px solid ${preset===key?C.teal:C.border}`, background:preset===key?C.tealLight:C.panel, transition:"all 0.13s" }}>
                <div style={{ fontSize:22, marginBottom:5 }}>{p.icon}</div>
                <div style={{ fontSize:12, fontWeight:preset===key?700:500, color:preset===key?C.teal:C.text, marginBottom:2 }}>{p.label}</div>
                <div style={{ fontSize:10, color:C.muted, lineHeight:1.4 }}>{p.desc}</div>
              </div>
            ))}
          </div>

          <SectionLabel label="Avoid" />
          <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
            <Chip label="Stroads" icon="🚗" active={prefs.avoid_stroads} onClick={() => toggle("avoid_stroads")} color={C.coral} bg={C.coralLight} />
            <Chip label="Bridges & Tunnels" icon="🌉" active={prefs.avoid_bridges} onClick={() => toggle("avoid_bridges")} color={C.coral} bg={C.coralLight} />
          </div>

          <SectionLabel label="Prefer" />
          <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
            <Chip label="Low Traffic" icon="🤫" active={prefs.prefer_low_traffic} onClick={() => toggle("prefer_low_traffic")} color={C.teal} bg={C.tealLight} />
            <Chip label="Dedicated Paths" icon="🚶" active={prefs.prefer_paths} onClick={() => toggle("prefer_paths")} color={C.teal} bg={C.tealLight} />
            <Chip label="Round Trip" icon="🔄" active={prefs.round_trip} onClick={() => toggle("round_trip")} color={C.teal} bg={C.tealLight} />
          </div>

          {envData && (
            <>
              <div style={{ height:"0.5px", background:C.border, margin:"16px 0" }} />
              <SectionLabel label="Live Conditions" first />
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                {[
                  { icon:"☀️", label:"UV Index",    value:(envData.uv_index??0).toFixed(1),                                    sub:uvLabel(envData.uv_index),          color:uvColor(envData.uv_index) },
                  { icon:"🌿", label:"Green Space", value:`${Math.round((envData.green_space_score??0)*100)}%`,                sub:greenLabel(envData.green_space_score), color:C.green },
                  { icon:"💨", label:"PM2.5",       value:`${(envData.air_quality_pm25??0).toFixed(1)} µg`,                   sub:aqLabel(envData.air_quality_pm25),   color:C.lavender },
                  { icon:"🔇", label:"Noise",       value:envData.noise_db!=null?`${Math.round(envData.noise_db)} dB`:"—",   sub:noiseLabel(envData.noise_db),        color:noiseColor(envData.noise_db) },
                ].map(({ icon, label, value, sub, color }) => (
                  <div key={label} style={{ background:C.panel, borderRadius:10, padding:"10px 12px", border:`0.5px solid ${C.border}`, animation:"fadeIn 0.4s ease" }}>
                    <div style={{ fontSize:11, color:C.muted, marginBottom:3 }}>{icon} {label}</div>
                    <div style={{ fontSize:15, fontWeight:700, color, lineHeight:1 }}>{value}</div>
                    <div style={{ fontSize:10, color, opacity:0.75, marginTop:3, fontWeight:600 }}>{sub}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {loading ? <LoadingState /> : routes.length > 0 && (
            <>
              <div style={{ height:"0.5px", background:C.border, margin:"16px 0" }} />
              {best && (
                <div style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"4px 12px", borderRadius:20, marginBottom:10, background:C.teal, color:C.yellow, fontSize:11, fontWeight:700, animation:"fadeIn 0.5s ease" }}>
                  ✦ {best.type} recommended
                </div>
              )}
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {routes.map((route, routeIdx) => {
                  const meta = ROUTES[route.type];
                  const isActive = activeRoute===route.type;
                  const isBest = best?.type===route.type;
                  const shadowRGB = ROUTE_SHADOW_RGB[route.type];
                  return (
                    <div key={route.type} className="rcard" onClick={() => handleSelectRoute(route.type)} style={{
                      borderRadius:12, padding:"12px 14px", cursor:"pointer",
                      border:`1.5px solid ${isActive?meta.color:C.border}`,
                      background:isActive?C.panel:C.panelAlt,
                      boxShadow:isActive?`0 4px 16px rgba(${shadowRGB}, 0.13)`:"none",
                      transition:"all 0.14s", animation:`fadeIn 0.4s ease ${routeIdx*0.08}s both`,
                    }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:36, height:36, borderRadius:9, flexShrink:0, background:meta.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17 }}>{meta.icon}</div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <span style={{ fontWeight:700, fontSize:13, color:C.text }}>{route.type}</span>
                            {isBest && <span style={{ fontSize:9, padding:"2px 6px", borderRadius:20, background:meta.bg, color:meta.color, fontWeight:700, textTransform:"uppercase" }}>Best</span>}
                          </div>
                          <div style={{ fontSize:11, color:C.muted, marginTop:1 }}>{meta.tag}</div>
                        </div>
                        <div style={{ textAlign:"right", flexShrink:0 }}>
                          <div style={{ fontSize:19, fontWeight:700, color:meta.color, lineHeight:1 }}>{Math.round(route.score)}</div>
                          <div style={{ fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:"0.4px", marginTop:1 }}>score</div>
                        </div>
                      </div>
                      {isActive && (
                        <div style={{ marginTop:10 }}>
                          <div style={{ background:C.border, borderRadius:3, height:3, overflow:"hidden", marginBottom:8 }}>
                            <div style={{ width:`${Math.round(route.score)}%`, height:"100%", background:meta.color, borderRadius:3, transition:"width 0.5s ease" }} />
                          </div>
                          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
                            <span style={{ fontSize:11, color:C.muted }}>{(route.distance_m/1000).toFixed(1)} km</span>
                            <span style={{ fontSize:11, color:C.muted }}>{Math.round(route.duration_s/60)} min walk</span>
                          </div>
                          <button onClick={e => { e.stopPropagation(); setShowDetail(true); setNavActive(false); }}
                            style={{ width:"100%", padding:"10px", background:meta.color, color:"white", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans', sans-serif", transition:"opacity 0.1s" }}
                            onMouseEnter={e => e.currentTarget.style.opacity="0.88"}
                            onMouseLeave={e => e.currentTarget.style.opacity="1"}>
                            View Route ›
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {!navActive && showDetail && activeObj && (
        <RouteDetail route={activeObj} isMobile={isMobile} onClose={() => setShowDetail(false)}
          onStartNav={() => { setShowDetail(false); setNavActive(true); setActiveSegmentCoords(null); }} />
      )}
      {navActive && activeObj && (
        <NavigationPanel route={activeObj} isMobile={isMobile}
          onClose={() => { setNavActive(false); setActiveSegmentCoords(null); setFitTarget(activeObj.geometry.coordinates.map(([lng,lat])=>[lat,lng])); }}
          onFitBounds={handleFitBounds} />
      )}
    </div>
  );
}